import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { Check } from 'lucide-react'
import { MANAGED_SERVICES, DFY_BUNDLES } from '@gjl/voice/pricing'

export default function DigitalPartnershipPage() {
  const breadcrumbs = generateBreadcrumbs('/services/digital-partnership', breadcrumbLabels)

  const managedWebsite = MANAGED_SERVICES.find((s) => s.id === 'managed-website')!
  const growthPartner = DFY_BUNDLES.find((b) => b.id === 'growth-partner')!
  const concierge = DFY_BUNDLES.find((b) => b.id === 'concierge')!

  useSEO({
    title: 'Ongoing Support | Year-Round Digital Partnership',
    description:
      'Year-round digital support for music organizations. Website updates, email campaigns, analytics, and strategy aligned with your season.',
    url: '/services/digital-partnership',
    canonical: '/services/digital-partnership',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Year-Round Digital Partnership',
      description:
        'Ongoing digital support for classical music organizations including website updates, email campaigns, and strategic consulting.',
      url: '/services/digital-partnership',
    }),
  })

  // Animation refs
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const problemsRef = useStaggeredGrid<HTMLDivElement>('.problem-card', { stagger: 0.08 })
  const tiersRef = useStaggeredGrid<HTMLDivElement>('.tier-card', { stagger: 0.12 })
  const includedRef = useScrollReveal<HTMLDivElement>('.included-item', { stagger: 0.08 })
  const timelineRef = useScrollReveal<HTMLDivElement>('.timeline-item', { stagger: 0.1 })
  const benefitsRef = useStaggeredGrid<HTMLDivElement>('.benefit-card', { stagger: 0.08 })
  const faqRef = useScrollReveal<HTMLDivElement>('.faq-item', { stagger: 0.08 })
  const ctaRef = useScrollReveal<HTMLDivElement>('.cta-content')

  return (
    <div className="mx-auto max-w-6xl space-y-24 px-4 py-16">
      {/* Hero Section */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <h1 className="text-fluid-h1">Ongoing Support for Music Organizations</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          Your organization doesn&apos;t stop after launching a new website. You need email campaigns during concert
          season. Website updates throughout the year. Analytics to track ticket sales. SEO improvements for next
          season.
        </p>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          Our managed services give you ongoing support tailored to your organization&apos;s rhythm, whether you&apos;re
          running a festival, orchestra, opera company, or choral organization. Focus on creating transformative musical
          experiences while we handle your digital strategy.
        </p>
        <p className="text-fluid-body text-foreground mx-auto max-w-2xl font-medium">
          Think of us as an extension of your team, someone who understands both the music and the tech.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call →
              </Button>
            </motion.div>
          </a>
        </div>
      </section>

      {/* Problem Section */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Why Music Organizations Need Year-Round Digital Support</h2>
        <div ref={problemsRef} className="grid gap-6 md:grid-cols-2">
          {[
            {
              problem: 'Seasonal Cycles & Evolving Needs',
              description:
                'Your digital needs change throughout the year. Concert season requires active email marketing. Summer needs website updates and season planning. You need someone who understands this rhythm.',
            },
            {
              problem: 'Ongoing Website Updates',
              description:
                'Concert dates change. Artists get added. Programs evolve. You need someone who can update your website quickly without waiting weeks for a developer to respond.',
            },
            {
              problem: 'Email Campaigns for Audience Engagement',
              description:
                "Season announcements. Ticket launch emails. Concert reminders. Donor campaigns. These don't write and send themselves. You need consistent campaign management.",
            },
            {
              problem: 'No Time for SEO or Analytics',
              description:
                'You know you should be tracking website traffic, ticket sales sources, and audience metrics. But who has time? You need someone monitoring this continuously.',
            },
            {
              problem: 'Technical Issues & Bug Fixes',
              description:
                "Forms break. Integrations stop working. Emails bounce. When tech issues happen during ticket launch week, you can't wait days for a fix. You need immediate support.",
            },
            {
              problem: 'No Strategic Partner',
              description:
                "You need someone who understands your organization's goals, tracks progress year over year, and proactively suggests improvements. Not just a vendor who builds once and disappears.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="problem-card border-border space-y-2 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{item.problem}</h3>
              <p className="text-muted-foreground text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
        <p className="text-muted-foreground mx-auto max-w-2xl pt-4 text-center italic">
          A one-time website build isn&apos;t enough. Organizations need ongoing digital support aligned with their
          season and operational cycle.
        </p>
      </section>

      {/* Managed Services Ladder */}
      <section className="mx-auto max-w-5xl space-y-8">
        <div className="space-y-4 text-center">
          <h2 className="text-fluid-h2">Pick Your Level of Support</h2>
          <p className="text-fluid-body text-muted mx-auto max-w-2xl">
            Every managed service includes the underlying software. Start with your website, add email when you are
            ready, or hand us the whole digital operation.
          </p>
        </div>

        <div ref={tiersRef} className="grid gap-6 md:grid-cols-3">
          {/* Managed Website */}
          <motion.div
            className="tier-card border-border bg-background/50 space-y-4 rounded-lg border p-6"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div>
              <h3 className="text-xl font-semibold text-white">{managedWebsite.name}</h3>
              <p className="text-primary mt-1 text-lg font-bold">${managedWebsite.priceMonthly}/month</p>
              <p className="text-muted-foreground mt-2 text-sm">
                Your website, kept current for you. The website software is included.
              </p>
            </div>
            <ul className="space-y-2 text-sm">
              {[
                'Unlimited update requests, one active at a time',
                '72-hour turnaround on every request',
                'Hosting, editor, custom domain, forms & SEO included',
                'Technical maintenance & bug fixes',
                'Analytics reporting',
              ].map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Growth Partner */}
          <motion.div
            className="tier-card border-primary bg-primary/5 space-y-4 rounded-lg border-2 p-6"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div className="text-primary text-xs font-semibold tracking-wider uppercase">Most Popular</div>
            <div>
              <h3 className="text-xl font-semibold text-white">{growthPartner.name}</h3>
              <p className="text-primary mt-1 text-lg font-bold">{growthPartner.price}</p>
              <p className="text-muted-foreground mt-2 text-sm">{growthPartner.note}</p>
            </div>
            <ul className="space-y-2 text-sm">
              {[
                'Everything in Managed Website',
                'We plan, write, design & send your email',
                'About 2 campaigns per month plus 1 automation',
                'Email software included',
                'Campaigns aligned with your season calendar',
              ].map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Concierge */}
          <motion.div
            className="tier-card border-border bg-background/50 space-y-4 rounded-lg border p-6"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div>
              <h3 className="text-xl font-semibold text-white">{concierge.name}</h3>
              <p className="text-primary mt-1 text-lg font-bold">{concierge.price}</p>
              <p className="text-muted-foreground mt-2 text-sm">{concierge.note}</p>
            </div>
            <ul className="space-y-2 text-sm">
              {[
                'Everything in Growth Partner',
                'Fundraising campaigns run for you',
                'Box office setup, on-sales & reporting handled',
                'Ongoing strategy & prioritization',
                'All underlying software included',
              ].map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        <div className="space-y-3 text-center">
          <p className="text-muted-foreground mx-auto max-w-3xl text-sm">
            Every managed service is also available on its own:{' '}
            {MANAGED_SERVICES.map((service, idx) => (
              <span key={service.id}>
                <Link to={`/services/done-for-you#${service.id}`} className="text-primary hover:underline">
                  {service.name}
                </Link>{' '}
                (${service.priceMonthly}/mo){idx < MANAGED_SERVICES.length - 1 ? ', ' : '. '}
              </span>
            ))}
            See the full menu on our{' '}
            <Link to="/services/done-for-you" className="text-primary hover:underline">
              Done-For-You Services
            </Link>{' '}
            page.
          </p>
          <p className="text-muted-foreground mx-auto max-w-3xl text-sm">
            Every plan starts with a one-time onboarding (the $250 Launch build counts as it) and a 3-month minimum
            term, then runs month to month. Leaving is always free, and you take a full export of your data with you.
          </p>
        </div>
      </section>

      {/* What's Included Across All Tiers */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">What&apos;s Included Across All Plans</h2>
        <p className="text-muted-foreground mx-auto max-w-2xl text-center">
          Core services that every managed plan includes.
        </p>

        <div ref={includedRef} className="space-y-6">
          {[
            {
              title: 'Season-Aligned Planning',
              description:
                "We align digital strategy with your organization's calendar, from season planning to concert launches to summer programming.",
            },
            {
              title: 'Ongoing Website Updates',
              description:
                'Concert announcements, artist bios, program changes, schedule adjustments, and content refreshes handled promptly.',
            },
            {
              title: 'Technical Maintenance',
              description:
                'Hosting, security updates, backups, SSL management, and performance monitoring, all managed for you.',
            },
            {
              title: 'Analytics & Reporting',
              description:
                "Track website traffic, ticket sales funnels, and audience engagement data to understand what's working.",
            },
            {
              title: 'Strategic Guidance',
              description:
                "Regular consultation on digital strategy, audience growth, and tech improvements tailored to your organization's goals.",
            },
            {
              title: 'Direct Access',
              description:
                'No ticketing systems or endless back-and-forth. Email, call, or Slack directly with someone who knows your organization.',
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="included-item border-primary bg-background/50 rounded-lg border p-5 backdrop-blur-sm"
              whileHover={{ x: 4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-lg font-semibold text-white">{item.title}</h3>
              <p className="text-muted-foreground mt-1 text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How It Works - Example for Concert Organizations */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Example: How a Partnership Works for a Concert Organization</h2>
        <div ref={timelineRef} className="space-y-6">
          {[
            {
              month: 'January-March: Planning Season',
              description:
                "We help plan your next season's digital strategy, update website content, and prepare email campaigns for season ticket sales launch.",
            },
            {
              month: 'April-May: Season Launch',
              description:
                'Email campaigns to announce new season and drive early ticket sales, SEO improvements to rank for concert searches, website updates with new repertoire and artists.',
            },
            {
              month: 'June-August: Summer Season',
              description:
                'Ongoing concert promotion emails, ticket sales tracking and optimization, quick updates for programming changes, technical support for any issues.',
            },
            {
              month: 'September-November: Fall Season',
              description:
                'Continue audience engagement campaigns, optimize donation appeals, update website with new programs and artist info.',
            },
            {
              month: 'December: Year-End & Strategic Planning',
              description:
                'Holiday concert promotions, year-end giving campaigns, review annual data, set digital goals for next season.',
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="timeline-item border-primary space-y-2 border-l-4 py-3 pl-6"
              whileHover={{ x: 4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{item.month}</h3>
              <p className="text-muted-foreground">{item.description}</p>
            </motion.div>
          ))}
        </div>
        <p className="text-muted-foreground pt-4 text-center italic">
          Your plan adapts to your organization&apos;s seasonal rhythm, not a generic monthly checklist.
        </p>
      </section>

      {/* Benefits Section */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Why Organizations Choose Partnership Support</h2>
        <div ref={benefitsRef} className="grid gap-6 md:grid-cols-2">
          {[
            {
              benefit: 'Predictable Monthly Investment',
              description:
                'No surprise invoices. One monthly price you can plan around. After the 3-month minimum, you are month to month.',
            },
            {
              benefit: 'Continuous Improvement',
              description:
                'We track metrics year over year and continuously optimize your website, email campaigns, and audience engagement strategies.',
            },
            {
              benefit: 'Faster Response Times',
              description:
                'Managed clients get priority support. Urgent bug fixes come first, and update requests turn around within 72 hours. No waiting weeks for availability.',
            },
            {
              benefit: 'Proactive Strategy',
              description:
                "We don't wait for you to ask. We proactively suggest improvements, identify opportunities, and keep you ahead of the curve.",
            },
            {
              benefit: 'Deep Organizational Knowledge',
              description:
                'After a few months, we know your programs, your audience, your goals. We become true partners, not just vendors.',
            },
            {
              benefit: 'Audience Growth Over Time',
              description:
                'Small optimizations compound. Email campaigns improve. SEO rankings increase. Ticket conversion rates rise. Audiences grow year over year.',
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="benefit-card border-primary/20 bg-primary/5 space-y-2 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{item.benefit}</h3>
              <p className="text-muted-foreground text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="mx-auto max-w-3xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Frequently Asked Questions</h2>
        <div ref={faqRef} className="space-y-6">
          {[
            {
              question: 'Can we start small and add services later?',
              answer: `Yes. Many organizations start with Managed Website and add Managed Email later, which becomes the Growth Partner bundle at ${growthPartner.price}. You can adjust your plan anytime.`,
            },
            {
              question: 'What are the terms?',
              answer:
                'Every done-for-you plan starts with a one-time onboarding (the $250 Launch build counts as it) and a 3-month minimum term. After that you are month to month. Leaving is always free, and you get a full export of your data.',
            },
            {
              question: 'Do we need to have our website built by you first?',
              answer:
                'Not necessarily. We can manage websites built elsewhere after a one-time onboarding. If we build your site, the $250 Launch build counts as your onboarding.',
            },
            {
              question: 'How do email campaigns work?',
              answer:
                "We write, design, and schedule email campaigns aligned with your organization's calendar. You review and approve before sending. We track open rates, click rates, and adjust strategy based on data.",
            },
            {
              question: 'Can we request additional features or projects during the plan?',
              answer:
                'Yes. On Managed Website, update requests are unlimited, one active at a time with a 72-hour turnaround. Larger projects (major redesigns, complex integrations) are scoped and quoted up front.',
            },
            {
              question: 'How is this different from hiring a full-time marketing person?',
              answer:
                "You get specialized classical music + digital marketing expertise for a fraction of the cost of a full-time hire. Plus, we bring technical skills (development, integrations, analytics) that most marketing staff don't have.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="faq-item border-primary space-y-2 border-l-2 pl-6"
              whileHover={{ x: 4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-lg font-semibold">{item.question}</h3>
              <p className="text-muted-foreground">{item.answer}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section
        ref={ctaRef}
        className="bg-primary/5 border-primary/20 mx-auto max-w-3xl space-y-6 rounded-lg border p-12 text-center"
      >
        <div className="cta-content space-y-6">
          <h2 className="text-fluid-h2">Ready to Grow Your Digital Presence?</h2>
          <p className="text-fluid-body text-muted mx-auto max-w-2xl">
            Schedule a free 15-minute strategy call to discuss your organization&apos;s needs and find the right plan
            for your goals.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
            <a href="/book" target="_blank" rel="noopener noreferrer">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                  Schedule Your Strategy Call →
                </Button>
              </motion.div>
            </a>
            <Link to="/contact">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                  Or Send Us a Message
                </Button>
              </motion.div>
            </Link>
          </div>
          <p className="text-muted-foreground text-sm">
            No sales pitch. Just an honest conversation about how ongoing support can help your organization grow.
          </p>
        </div>
      </section>
    </div>
  )
}
